import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent } from '@shopify/events';

/** Provisional hexes from BRIEF.md — used only for generated placeholders and thumbnail tinting. */
const COLOR_HEX = {
  Taupe: '#8F857A',
  Terracotta: '#B5654A',
  Saumon: '#DFA189',
  'Vert sauge': '#9CA58C',
  'Bleu marine': '#2B3A55',
  Doré: '#B79A5C',
  'Graphite mat': '#3D4043',
};

const COLORS = Object.keys(COLOR_HEX);
const MECHANISM_TYPES = ['interrupteur-simple', 'interrupteur-double', 'prise-simple', 'prise-double'];
const THUMBNAIL_COLOR = 'Graphite mat';

/**
 * Builds a data-URI SVG placeholder for a missing product image, tinted with the
 * selection's own color so a missing file never looks like a broken image.
 * @param {string} hex
 * @returns {string}
 */
function generatePlaceholder(hex) {
  const fill = hex || '#E2DBD2';
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">` +
    `<circle cx="200" cy="200" r="150" fill="${fill}" opacity="0.35"/>` +
    `<circle cx="200" cy="200" r="150" fill="none" stroke="${fill}" stroke-width="3" stroke-dasharray="10 8"/>` +
    `</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

/**
 * @typedef {{ left: string, right: string }} SlotValue
 */

class ProductConfigurator extends Component {
  /** @type {any} */
  #data;

  /** @type {boolean} */
  #debugEnabled = false;

  /** @type {NodeListOf<HTMLButtonElement>} */
  #formatButtons;
  /** @type {NodeListOf<HTMLButtonElement>} */
  #emplacementButtons;
  /** @type {NodeListOf<HTMLButtonElement>} */
  #mechanismButtons;
  /** @type {HTMLButtonElement[]} */
  #mechanismColorButtons = [];
  /** @type {HTMLButtonElement[]} */
  #plateColorButtons = [];
  /** @type {HTMLButtonElement[]} */
  #wallButtons = [];

  #state = {
    format: '1-poste',
    activeSlot: 'left',
    mechanism: { left: 'prise-simple', right: 'interrupteur-simple' },
    mechanismColor: { left: 'Taupe', right: 'Taupe' },
    plateColor: 'Taupe',
    wallIndex: 0,
    qty: { left: 1, right: 1, plate: 1 },
    qtyLinked: true,
  };

  connectedCallback() {
    super.connectedCallback();

    const dataScript = this.querySelector('[data-configurator-json]');
    if (!(dataScript instanceof HTMLScriptElement)) {
      this.#showFatalError(
        'Le bloc de données du configurateur est introuvable dans la page (balise <script data-configurator-json> absente).'
      );
      return;
    }

    try {
      this.#data = JSON.parse(dataScript.textContent ?? '{}');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const raw = dataScript.textContent ?? '';
      const context = this.#extractJsonErrorContext(raw, message);
      this.#showFatalError(`Les données du configurateur ne sont pas un JSON valide : ${message}`, context);
      return;
    }

    this.#debugEnabled = new URLSearchParams(window.location.search).has('configurator_debug');
    if (this.#debugEnabled && this.refs.debugOverlay) {
      this.refs.debugOverlay.hidden = false;
      if (this.refs.debugReadout) this.refs.debugReadout.hidden = false;
      window.addEventListener('resize', () => this.#renderDebug());
    }

    try {
      this.#formatButtons = this.querySelectorAll('[data-format]');
      this.#emplacementButtons = this.querySelectorAll('[data-emplacement]');
      this.#mechanismButtons = this.querySelectorAll('[data-mechanism]');

      this.#bindStaticControls();
      this.#buildSwatches();
      this.#setThumbnails();
      this.#applyPreset(0, { resetQty: true });
    } catch (error) {
      this.#showFatalError(
        `Erreur lors de l'initialisation du configurateur : ${error instanceof Error ? error.message : error}`
      );
      if (error instanceof Error) console.error(error);
    }
  }

  /**
   * Renders a visible, French error banner at the top of the component so failures are
   * diagnosable from the page itself, without needing browser DevTools.
   * @param {string} message
   * @param {string} [detail]
   */
  #showFatalError(message, detail) {
    console.error('Configurateur :', message);
    const banner = document.createElement('div');
    banner.setAttribute('role', 'alert');
    banner.style.cssText =
      'background:#fdecea;border:1px solid #e3897c;color:#611a15;padding:16px;border-radius:8px;margin-block-end:24px;font-size:0.875rem;';
    const title = document.createElement('p');
    title.style.margin = '0';
    title.innerHTML = `<strong>Configurateur — erreur technique</strong><br>${message}`;
    banner.appendChild(title);
    if (detail) {
      const pre = document.createElement('pre');
      pre.style.cssText = 'white-space:pre-wrap;margin:8px 0 0;font-size:0.75rem;overflow:auto;';
      pre.textContent = detail;
      banner.appendChild(pre);
    }
    this.prepend(banner);
  }

  /**
   * Pulls the text around a JSON.parse failure out of the raw source, using whichever location
   * the engine reported (Firefox: "line X column Y", Chrome/Safari: "position N"), so the debug
   * banner shows the actual failure instead of just the start of a (possibly huge) string.
   * @param {string} raw
   * @param {string} errorMessage
   * @returns {string}
   */
  #extractJsonErrorContext(raw, errorMessage) {
    const lineColMatch = errorMessage.match(/line (\d+) column (\d+)/i);
    const positionMatch = errorMessage.match(/position (\d+)/i);
    const lines = raw.split('\n');
    let lineIndex = -1;
    let column = 0;

    if (lineColMatch) {
      lineIndex = Number(lineColMatch[1]) - 1;
      column = Number(lineColMatch[2]);
    } else if (positionMatch) {
      let offset = Number(positionMatch[1]);
      for (let i = 0; i < lines.length; i++) {
        const lineLength = lines[i].length + 1;
        if (offset < lineLength) {
          lineIndex = i;
          column = offset + 1;
          break;
        }
        offset -= lineLength;
      }
    }

    if (lineIndex < 0 || lineIndex >= lines.length) {
      return raw.length > 800 ? `${raw.slice(0, 800)}…` : raw;
    }

    const start = Math.max(0, lineIndex - 2);
    const end = Math.min(lines.length, lineIndex + 3);
    const excerpt = lines
      .slice(start, end)
      .map((line, i) => {
        const actualLineNumber = start + i + 1;
        const marker = actualLineNumber === lineIndex + 1 ? '>> ' : '   ';
        return `${marker}${actualLineNumber}: ${line}`;
      })
      .join('\n');
    const pointer = column > 0 ? `\n${' '.repeat(String(lineIndex + 1).length + 5 + column - 1)}^` : '';

    return `${excerpt}${pointer}`;
  }

  #bindStaticControls() {
    for (const button of this.#formatButtons) {
      button.addEventListener('click', () => this.#setFormat(button.dataset.format ?? '1-poste'));
    }

    for (const button of this.#emplacementButtons) {
      button.addEventListener('click', () => this.#setActiveSlot(button.dataset.emplacement === 'right' ? 'right' : 'left'));
    }

    for (const button of this.#mechanismButtons) {
      button.addEventListener('click', () => this.#setMechanism(button.dataset.mechanism ?? 'prise-simple'));
    }

    for (const button of this.querySelectorAll('[data-preset-index]')) {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.presetIndex);
        if (!Number.isNaN(index)) this.#applyPreset(index, { resetQty: true });
      });
    }

    this.refs.qtyLinkToggle?.addEventListener('click', () => this.#toggleQtyLink());
    this.refs.addToCart?.addEventListener('click', () => this.#handleAddToCart());
    this.refs.continueConfiguring?.addEventListener('click', () => this.#handleContinueConfiguring());
  }

  #buildSwatches() {
    const mechanismContainer = this.refs.mechanismColorPicker;
    const plateContainer = this.refs.plateColorPicker;
    const wallContainer = this.refs.wallPicker;

    if (mechanismContainer instanceof HTMLElement) {
      this.#mechanismColorButtons = COLORS.map((color) =>
        this.#createSwatch(color, COLOR_HEX[color], `Couleur du mécanisme : ${color}`, mechanismContainer, () =>
          this.#setMechanismColor(color)
        )
      );
    }

    if (plateContainer instanceof HTMLElement) {
      this.#plateColorButtons = COLORS.map((color) =>
        this.#createSwatch(color, COLOR_HEX[color], `Couleur de la plaque : ${color}`, plateContainer, () =>
          this.#setPlateColor(color)
        )
      );
    }

    if (wallContainer instanceof HTMLElement && Array.isArray(this.#data.wallColors)) {
      this.#wallButtons = this.#data.wallColors.map((wall, index) =>
        this.#createSwatch(wall.label, wall.hex, `Fond mural : ${wall.label}`, wallContainer, () => this.#setWall(index))
      );
    }
  }

  /**
   * @param {string} value
   * @param {string} hex
   * @param {string} ariaLabel
   * @param {HTMLElement} container
   * @param {() => void} onClick
   */
  #createSwatch(value, hex, ariaLabel, container, onClick) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'configurator-swatch';
    button.style.backgroundColor = hex || '#E2DBD2';
    button.title = value;
    button.setAttribute('aria-label', ariaLabel);
    button.setAttribute('aria-pressed', 'false');
    button.addEventListener('click', onClick);
    container.appendChild(button);
    return button;
  }

  #setThumbnails() {
    for (const button of this.#mechanismButtons) {
      const type = button.dataset.mechanism;
      const img = button.querySelector('img');
      if (!type || !(img instanceof HTMLImageElement)) continue;
      this.#setImage(img, this.#mechanismImageUrl(type, THUMBNAIL_COLOR), this.#data.mechanismLabels?.[type] ?? type, THUMBNAIL_COLOR);
    }
  }

  /**
   * @param {number} index
   * @param {{ resetQty?: boolean }} [options]
   */
  #applyPreset(index, options = {}) {
    const preset = this.#data.presets?.[index];
    if (!preset) return;

    this.#state.format = preset.format;
    this.#state.activeSlot = 'left';
    this.#state.mechanism = { left: preset.mechanismLeft, right: preset.mechanismRight };
    this.#state.mechanismColor = { left: preset.colorLeft, right: preset.colorRight };
    this.#state.plateColor = preset.plateColor;
    this.#state.wallIndex = preset.wallIndex ?? 0;

    if (options.resetQty) {
      this.#state.qty = { left: 1, right: 1, plate: 1 };
      this.#state.qtyLinked = true;
      if (this.refs.qtyLinkToggle) {
        this.refs.qtyLinkToggle.setAttribute('aria-pressed', 'true');
        if (this.refs.qtyLinkLabel) this.refs.qtyLinkLabel.textContent = 'Quantités liées';
        if (this.refs.qtyLinkIcon) this.refs.qtyLinkIcon.textContent = '🔗';
      }
    }

    if (this.refs.postAdd) this.refs.postAdd.hidden = true;

    this.#render();
  }

  #setFormat(format) {
    this.#state.format = format === '2-postes' ? '2-postes' : '1-poste';
    if (this.#state.format === '1-poste') this.#state.activeSlot = 'left';
    this.#render();
  }

  /** @param {'left'|'right'} slot */
  #setActiveSlot(slot) {
    this.#state.activeSlot = slot;
    this.#render();
  }

  #setMechanism(type) {
    this.#state.mechanism[this.#state.activeSlot] = type;
    this.#render();
  }

  #setMechanismColor(color) {
    this.#state.mechanismColor[this.#state.activeSlot] = color;
    this.#render();
  }

  #setPlateColor(color) {
    this.#state.plateColor = color;
    this.#render();
  }

  #setWall(index) {
    this.#state.wallIndex = index;
    this.#render();
  }

  #toggleQtyLink() {
    this.#state.qtyLinked = !this.#state.qtyLinked;
    const pressed = this.#state.qtyLinked;

    this.refs.qtyLinkToggle?.setAttribute('aria-pressed', String(pressed));
    if (this.refs.qtyLinkIcon) this.refs.qtyLinkIcon.textContent = pressed ? '🔗' : '⛓️‍💥';
    if (this.refs.qtyLinkLabel) this.refs.qtyLinkLabel.textContent = pressed ? 'Quantités liées' : 'Quantités indépendantes';

    if (pressed) {
      const lines = this.#getLines();
      const reference = this.#state.qty[lines[0]?.key] ?? 1;
      for (const line of lines) this.#state.qty[line.key] = reference;
    }

    this.#renderPurchase();
  }

  /**
   * @param {string} key
   * @param {number} delta
   */
  #changeQty(key, delta) {
    if (this.#state.qtyLinked) {
      const next = Math.max(1, (this.#state.qty[key] ?? 1) + delta);
      for (const line of this.#getLines()) this.#state.qty[line.key] = next;
    } else {
      this.#state.qty[key] = Math.max(1, (this.#state.qty[key] ?? 1) + delta);
    }
    this.#renderPurchase();
  }

  #render() {
    this.#renderStage();
    this.#renderControls();
    this.#renderPurchase();
    if (this.#debugEnabled) this.#renderDebug();
  }

  /**
   * Draws the *actual* rendered CSS boxes for the plate and active mechanism image, measured
   * via getBoundingClientRect (not assumed from settings), plus a text readout. Only runs when
   * ?configurator_debug=1 is in the URL.
   */
  #renderDebug() {
    const stageEl = this.refs.stage;
    const plateImg = this.refs.plateImg;
    const mechanismImg = this.#state.activeSlot === 'right' ? this.refs.mechanismRightImg : this.refs.mechanismLeftImg;
    if (!(stageEl instanceof HTMLElement) || !(plateImg instanceof HTMLImageElement) || !(mechanismImg instanceof HTMLImageElement)) {
      return;
    }

    const stageRect = stageEl.getBoundingClientRect();
    const plateRect = plateImg.getBoundingClientRect();
    const mechRect = mechanismImg.getBoundingClientRect();

    const relative = (rect) => ({
      left: rect.left - stageRect.left,
      top: rect.top - stageRect.top,
      width: rect.width,
      height: rect.height,
    });

    const plateBox = relative(plateRect);
    const mechBox = relative(mechRect);

    if (this.refs.debugPlateBox instanceof HTMLElement) {
      Object.assign(this.refs.debugPlateBox.style, {
        left: `${plateBox.left}px`,
        top: `${plateBox.top}px`,
        width: `${plateBox.width}px`,
        height: `${plateBox.height}px`,
      });
      const label = this.refs.debugPlateBox.querySelector('span');
      if (label) label.textContent = `plate ${plateBox.width.toFixed(0)}×${plateBox.height.toFixed(0)}`;
    }

    if (this.refs.debugMechanismBox instanceof HTMLElement) {
      Object.assign(this.refs.debugMechanismBox.style, {
        left: `${mechBox.left}px`,
        top: `${mechBox.top}px`,
        width: `${mechBox.width}px`,
        height: `${mechBox.height}px`,
      });
      const label = this.refs.debugMechanismBox.querySelector('span');
      if (label) label.textContent = `mechanism ${mechBox.width.toFixed(0)}×${mechBox.height.toFixed(0)}`;
    }

    if (this.refs.debugReadout instanceof HTMLElement) {
      const computed = getComputedStyle(stageEl);
      const scaleVar = computed.getPropertyValue('--mechanism-scale').trim();
      const offsetVar = computed.getPropertyValue('--emplacement-offset').trim();
      const ratio = mechBox.width / plateBox.width;

      this.refs.debugReadout.textContent = [
        `stage box:      ${stageRect.width.toFixed(0)} x ${stageRect.height.toFixed(0)} px`,
        `plate box:      ${plateBox.width.toFixed(0)} x ${plateBox.height.toFixed(0)} px (CSS: width/height 100%, object-fit contain over a 2000x2000 asset at 88% fill)`,
        `mechanism box:  ${mechBox.width.toFixed(0)} x ${mechBox.height.toFixed(0)} px (CSS: width/height var(--mechanism-scale))`,
        `--mechanism-scale (from "Échelle du mécanisme" setting): ${scaleVar}`,
        `--emplacement-offset (from "Écart gauche/droite" setting): ${offsetVar}`,
        `measured mechanism-width / plate-width ratio: ${(ratio * 100).toFixed(1)}%`,
        `expected ratio at current --mechanism-scale: ~${scaleVar}`,
      ].join('\n');
    }
  }

  #renderStage() {
    const { format, activeSlot, mechanism, mechanismColor, plateColor, wallIndex } = this.#state;
    const wall = this.#data.wallColors?.[wallIndex];

    if (this.refs.wall instanceof HTMLElement) {
      this.refs.wall.style.backgroundColor = wall?.hex ?? '#FFFFFF';
    }

    if (this.refs.leftColumn instanceof HTMLElement) {
      this.refs.leftColumn.style.backgroundColor = wall?.hex ?? 'transparent';
    }

    const mechanismLabel = this.#data.mechanismLabels?.[mechanism.left] ?? mechanism.left;
    if (this.refs.mechanismLeftImg instanceof HTMLImageElement) {
      this.#setImage(
        this.refs.mechanismLeftImg,
        this.#mechanismImageUrl(mechanism.left, mechanismColor.left),
        `${mechanismLabel}, coloris ${mechanismColor.left}`,
        mechanismColor.left
      );
    }

    const isDouble = format === '2-postes';
    if (this.refs.stage instanceof HTMLElement) this.refs.stage.dataset.format = format;
    if (this.refs.mechanismRight instanceof HTMLElement) this.refs.mechanismRight.hidden = !isDouble;

    if (isDouble && this.refs.mechanismRightImg instanceof HTMLImageElement) {
      const rightLabel = this.#data.mechanismLabels?.[mechanism.right] ?? mechanism.right;
      this.#setImage(
        this.refs.mechanismRightImg,
        this.#mechanismImageUrl(mechanism.right, mechanismColor.right),
        `${rightLabel}, coloris ${mechanismColor.right}`,
        mechanismColor.right
      );
    }

    if (this.refs.plateImg instanceof HTMLImageElement) {
      this.#setImage(
        this.refs.plateImg,
        this.#plateImageUrl(format, plateColor),
        `Plaque de finition ${isDouble ? '2 postes' : '1 poste'}, coloris ${plateColor}`,
        plateColor
      );
    }

    // A pre-assembled combo photo (mechanism + plate shot as one piece) takes over from the
    // live two-layer composite when one exists. Two candidates are tried in order: an exact
    // two-colour combo (this mechanism colour with this plate colour), then a same-colour
    // combo (matching mechanism+plate). Each 404 falls through to the next, ending in the
    // live composite, so partially-covered combo sets degrade gracefully.
    const comboCandidates = isDouble
      ? []
      : [
          this.#comboImageUrl(mechanism.left, mechanismColor.left, plateColor),
          mechanismColor.left === plateColor ? this.#comboImageUrl(mechanism.left, mechanismColor.left) : null,
        ].filter((url, index, all) => url && all.indexOf(url) === index);

    if (this.refs.comboImg instanceof HTMLImageElement) {
      const comboImg = this.refs.comboImg;
      const showComposite = () => {
        comboImg.hidden = true;
        if (this.refs.plateImg instanceof HTMLElement) this.refs.plateImg.hidden = false;
        if (this.refs.mechanismLeft instanceof HTMLElement) this.refs.mechanismLeft.hidden = false;
      };
      const showCombo = () => {
        comboImg.hidden = false;
        if (this.refs.plateImg instanceof HTMLElement) this.refs.plateImg.hidden = true;
        if (this.refs.mechanismLeft instanceof HTMLElement) this.refs.mechanismLeft.hidden = true;
      };

      if (comboCandidates.length > 0) {
        let attempt = 0;
        const tryNext = () => {
          if (attempt >= comboCandidates.length) {
            comboImg.onerror = null;
            showComposite();
            return;
          }
          comboImg.src = comboCandidates[attempt];
          attempt += 1;
        };
        comboImg.onload = showCombo;
        comboImg.onerror = tryNext;
        comboImg.alt = `${mechanismLabel}, coloris ${mechanismColor.left}, plaque coloris ${plateColor}`;
        tryNext();
      } else {
        comboImg.onerror = null;
        comboImg.onload = null;
        comboImg.removeAttribute('src');
        showComposite();
      }
    }

    if (this.refs.ringLeft instanceof HTMLElement) this.refs.ringLeft.hidden = !(isDouble && activeSlot === 'left');
    if (this.refs.ringRight instanceof HTMLElement) this.refs.ringRight.hidden = !(isDouble && activeSlot === 'right');
  }

  #renderControls() {
    const { format, activeSlot, mechanism, mechanismColor, plateColor, wallIndex } = this.#state;
    const isDouble = format === '2-postes';

    for (const button of this.#formatButtons) {
      button.setAttribute('aria-pressed', String(button.dataset.format === format));
    }

    if (this.refs.emplacementField instanceof HTMLElement) this.refs.emplacementField.hidden = !isDouble;
    for (const button of this.#emplacementButtons) {
      const slot = button.dataset.emplacement === 'right' ? 'right' : 'left';
      button.setAttribute('aria-pressed', String(slot === activeSlot));
    }

    const activeMechanism = mechanism[activeSlot];
    for (const button of this.#mechanismButtons) {
      button.setAttribute('aria-pressed', String(button.dataset.mechanism === activeMechanism));
    }

    const activeMechanismColor = mechanismColor[activeSlot];
    for (const button of this.#mechanismColorButtons) {
      button.setAttribute('aria-pressed', String(button.title === activeMechanismColor));
    }

    for (const button of this.#plateColorButtons) {
      button.setAttribute('aria-pressed', String(button.title === plateColor));
    }

    this.#wallButtons.forEach((button, index) => {
      button.setAttribute('aria-pressed', String(index === wallIndex));
    });

    this.#renderCompatMessage();
  }

  #renderCompatMessage() {
    const el = this.refs.compatMessage;
    if (!(el instanceof HTMLElement)) return;

    const { format, activeSlot, mechanism } = this.#state;

    if (mechanism[activeSlot] === 'prise-double') {
      el.hidden = false;
      el.textContent = 'Une seule boîte d’encastrement, deux prises';
    } else if (format === '2-postes') {
      el.hidden = false;
      el.textContent = 'Deux boîtes d’encastrement';
    } else {
      el.hidden = true;
      el.textContent = '';
    }
  }

  /** @returns {{ key: string, productKey: string, color: string, label: string }[]} */
  #getLines() {
    const { format, mechanism, mechanismColor, plateColor } = this.#state;
    const lines = [
      {
        key: 'left',
        productKey: mechanism.left,
        color: mechanismColor.left,
        label: this.#data.mechanismLabels?.[mechanism.left] ?? mechanism.left,
      },
    ];

    if (format === '2-postes') {
      lines.push({
        key: 'right',
        productKey: mechanism.right,
        color: mechanismColor.right,
        label: this.#data.mechanismLabels?.[mechanism.right] ?? mechanism.right,
      });
    }

    lines.push({
      key: 'plate',
      productKey: format === '2-postes' ? 'plaque-2-postes' : 'plaque-1-poste',
      color: plateColor,
      label: format === '2-postes' ? 'Plaque de finition ronde 2 postes' : 'Plaque de finition ronde 1 poste',
    });

    return lines;
  }

  #renderPurchase() {
    const container = this.refs.purchaseLines;
    if (!(container instanceof HTMLElement)) return;

    container.textContent = '';

    const lines = this.#getLines();
    let anyBlocking = false;
    let total = 0;

    for (const line of lines) {
      const product = this.#data.products?.[line.productKey];
      const variant = product?.variants?.[line.color] ?? null;
      const qty = this.#state.qty[line.key] ?? 1;
      const blocked = !product || !variant || !variant.available;
      if (blocked) anyBlocking = true;
      else total += variant.price * qty;

      container.appendChild(this.#buildLineRow(line, variant, qty, blocked));
    }

    if (this.refs.totalPrice) {
      this.refs.totalPrice.textContent = formatMoney(total, this.#data.moneyFormat, this.#data.currencyCode);
    }

    if (this.refs.addToCart instanceof HTMLButtonElement) this.refs.addToCart.disabled = anyBlocking;

    if (this.refs.purchaseError instanceof HTMLElement) {
      this.refs.purchaseError.hidden = !anyBlocking;
      this.refs.purchaseError.textContent = anyBlocking
        ? 'Cette combinaison n’est pas disponible à l’achat pour le moment.'
        : '';
    }
  }

  /**
   * @param {{key: string, productKey: string, color: string, label: string}} line
   * @param {any} variant
   * @param {number} qty
   * @param {boolean} blocked
   */
  #buildLineRow(line, variant, qty, blocked) {
    const row = document.createElement('div');
    row.className = 'configurator-line';

    const img = document.createElement('img');
    img.width = 56;
    img.height = 56;
    img.loading = 'lazy';
    img.alt = `${line.label}, coloris ${line.color}`;
    this.#setImage(img, variant?.image ?? null, img.alt, line.color);
    row.appendChild(img);

    const info = document.createElement('div');
    info.className = 'configurator-line__info';

    const name = document.createElement('span');
    name.className = 'configurator-line__name';
    name.textContent = `${line.label} — ${line.color}`;
    info.appendChild(name);

    if (!variant) {
      const note = document.createElement('span');
      note.className = 'configurator-line__unavailable';
      note.textContent = 'Indisponible — combinaison non configurée côté boutique';
      info.appendChild(note);
    } else {
      const meta = document.createElement('span');
      meta.className = 'configurator-line__meta';
      meta.textContent = `Réf. ${variant.sku} · ${variant.priceFormatted}`;
      info.appendChild(meta);

      if (!variant.available) {
        const note = document.createElement('span');
        note.className = 'configurator-line__unavailable';
        note.textContent = 'Rupture de stock';
        info.appendChild(note);
      }
    }

    row.appendChild(info);
    row.appendChild(this.#buildQtyStepper(line.key, qty, blocked));

    return row;
  }

  /**
   * @param {string} key
   * @param {number} qty
   * @param {boolean} disabled
   */
  #buildQtyStepper(key, qty, disabled) {
    const wrap = document.createElement('div');
    wrap.className = 'configurator-qty';

    const minus = document.createElement('button');
    minus.type = 'button';
    minus.textContent = '−';
    minus.setAttribute('aria-label', 'Diminuer la quantité');
    minus.disabled = disabled;
    minus.addEventListener('click', () => this.#changeQty(key, -1));

    const value = document.createElement('span');
    value.className = 'configurator-qty__value';
    value.textContent = String(qty);
    value.setAttribute('aria-live', 'polite');

    const plus = document.createElement('button');
    plus.type = 'button';
    plus.textContent = '+';
    plus.setAttribute('aria-label', 'Augmenter la quantité');
    plus.disabled = disabled;
    plus.addEventListener('click', () => this.#changeQty(key, 1));

    wrap.append(minus, value, plus);
    return wrap;
  }

  /**
   * @param {HTMLImageElement} img
   * @param {string|null} url
   * @param {string} alt
   * @param {string} color
   */
  #setImage(img, url, alt, color) {
    img.alt = alt;
    img.onerror = () => {
      img.onerror = null;
      img.src = generatePlaceholder(COLOR_HEX[color]);
    };
    img.src = url || generatePlaceholder(COLOR_HEX[color]);
  }

  #mechanismImageUrl(type, color) {
    const slug = this.#data.colorSlugs?.[color];
    if (!slug) return null;
    return this.#data.assetUrls?.[`mecanisme-${type}-${slug}.png`] ?? null;
  }

  #plateImageUrl(format, color) {
    const slug = this.#data.colorSlugs?.[color];
    if (!slug) return null;
    const productKey = format === '2-postes' ? '2-postes' : '1-poste';
    return this.#data.assetUrls?.[`plaque-${productKey}-${slug}.png`] ?? null;
  }

  /**
   * Pre-assembled mechanism+plate photo, when one exists. With one colour, looks up the
   * same-colour combo (mechanism-<slug>.png). With two, looks up the exact mechanism/plate
   * colour pair (mechanism-<mechSlug>-<plateSlug>.png).
   */
  #comboImageUrl(type, mechColor, plateColor) {
    if (!this.#data.comboMechanisms?.includes(type)) return null;
    const mechSlug = this.#data.colorSlugs?.[mechColor];
    if (!mechSlug) return null;
    if (plateColor) {
      const plateSlug = this.#data.colorSlugs?.[plateColor];
      if (!plateSlug) return null;
      return this.#data.comboAssetUrls?.[`combo-${type}-${mechSlug}-${plateSlug}.png`] ?? null;
    }
    return this.#data.comboAssetUrls?.[`combo-${type}-${mechSlug}.png`] ?? null;
  }

  #buildSummary() {
    const { format, mechanism, mechanismColor, plateColor, wallIndex } = this.#state;
    const wallLabel = this.#data.wallColors?.[wallIndex]?.label ?? '';
    const parts = [`${this.#data.mechanismLabels?.[mechanism.left] ?? mechanism.left} ${mechanismColor.left}`];

    if (format === '2-postes') {
      parts.push(`${this.#data.mechanismLabels?.[mechanism.right] ?? mechanism.right} ${mechanismColor.right}`);
    }

    parts.push(`Plaque ${format === '2-postes' ? '2 postes' : '1 poste'} ${plateColor}`);

    return wallLabel ? `${parts.join(' + ')} — mur ${wallLabel}` : parts.join(' + ');
  }

  async #handleAddToCart() {
    const lines = this.#getLines();
    const items = [];

    for (const line of lines) {
      const product = this.#data.products?.[line.productKey];
      const variant = product?.variants?.[line.color];
      if (!variant || !variant.available) return;

      items.push({
        id: variant.id,
        quantity: this.#state.qty[line.key] ?? 1,
        properties: { 'Combinaison configurée': this.#buildSummary() },
      });
    }

    if (items.length === 0 || !(this.refs.addToCart instanceof HTMLButtonElement)) return;

    const originalLabel = this.refs.addToCart.textContent;
    this.refs.addToCart.disabled = true;
    this.refs.addToCart.textContent = 'Ajout en cours…';

    if (this.refs.purchaseError instanceof HTMLElement) this.refs.purchaseError.hidden = true;

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Erreur lors de l’ajout au projet.');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });

      if (this.refs.postAdd instanceof HTMLElement) this.refs.postAdd.hidden = false;
    } catch (error) {
      if (this.refs.purchaseError instanceof HTMLElement) {
        this.refs.purchaseError.hidden = false;
        this.refs.purchaseError.textContent =
          error instanceof Error ? error.message : 'Une erreur est survenue. Merci de réessayer.';
      }
      deferred.reject?.(error);
    } finally {
      this.refs.addToCart.disabled = false;
      this.refs.addToCart.textContent = originalLabel;
    }
  }

  #handleContinueConfiguring() {
    if (this.refs.postAdd instanceof HTMLElement) this.refs.postAdd.hidden = true;
    document.getElementById('cart-drawer')?.close?.();
  }
}

if (!customElements.get('product-configurator')) {
  customElements.define('product-configurator', ProductConfigurator);
}
